import { createAction } from 'redux-api-middleware';
import { getHeaders } from '@core/actions/xhrRequest';

export const FETCH_BIDS_INIT = 'FETCH_BIDS_INIT';
export const FETCH_BIDS_SUCCESS = 'FETCH_BIDS_SUCCESS';
export const FETCH_BIDS_FAILURE = 'FETCH_BIDS_FAILURE';

export const ACCEPT_BID_INIT = 'ACCEPT_BID_INIT';
export const ACCEPT_BID_SUCCESS = 'ACCEPT_BID_SUCCESS';
export const ACCEPT_BID_FAILURE = 'ACCEPT_BID_FAILURE';

export const fetchBids = (uniqueNumber) => createAction({
    // `meta.uniqueNumber` carried on every phase so the reducer can
    // compare against whatever order is currently in state and flush
    // stale bids when the customer switches to a new order (e.g. New
    // Order → Proceed to bidding). Without this meta the previous
    // order's writer cards flash for ~3s while the fresh fetch
    // resolves, which looks like a bug.
    types: [
        { type: FETCH_BIDS_INIT, meta: { uniqueNumber } },
        { type: FETCH_BIDS_SUCCESS, meta: { uniqueNumber } },
        { type: FETCH_BIDS_FAILURE, meta: { uniqueNumber } },
    ],
    endpoint: `/api/bidding/orders/${uniqueNumber}/bids`,
    method: 'GET',
    headers: getHeaders(),
    credentials: 'include',
});

export const acceptBid = (uniqueNumber, bidId) => createAction({
    types: [ACCEPT_BID_INIT, ACCEPT_BID_SUCCESS, ACCEPT_BID_FAILURE],
    endpoint: `/api/bidding/orders/${uniqueNumber}/accept-bid/${bidId}`,
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
});

export const DECLINE_BID_INIT = 'DECLINE_BID_INIT';
export const DECLINE_BID_SUCCESS = 'DECLINE_BID_SUCCESS';
export const DECLINE_BID_FAILURE = 'DECLINE_BID_FAILURE';

export const declineBid = (uniqueNumber, bidId) => createAction({
    types: [
        { type: DECLINE_BID_INIT, meta: { bidId } },
        { type: DECLINE_BID_SUCCESS, meta: { bidId } },
        { type: DECLINE_BID_FAILURE, meta: { bidId } },
    ],
    endpoint: `/api/bidding/orders/${uniqueNumber}/decline-bid/${bidId}`,
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
});

// Local presence patch — dispatched from the chat panel whenever the
// frontend knows something about a writer's state without needing a
// backend round-trip. Three call sites:
//   1. Customer just sent a message → flip `is_typing` true for a few
//      seconds, then false (synthetic "writer is composing" feel).
//   2. A writer message arrives via Mercure (real reply from support or
//      the stub-triggered auto-reply) → `is_online: true, last_seen: now,
//      is_typing: false` (writer is "here").
//   3. Ambient shuffle (ChatDock) → occasionally flips a random writer's
//      online state so the roster feels alive even when idle.
//
// There is NO presence-broadcast channel on the backend — presence is a
// frontend-owned concept. Keeping it local means zero extra Mercure
// publishes per chat event and no sync state to drift between backends.
//
// Payload keys are OPTIONAL: callers dispatch only what they directly
// observe (e.g. a typing-start event sends `{isTyping: true}` alone).
// Fields left out preserve their prior value in the reducer.
export const WRITER_PRESENCE_UPDATE = 'WRITER_PRESENCE_UPDATE';

// Invariant: a writer who's TYPING is by definition online. The action
// creator enforces that so every call site can send a bare
// `{bidId, isTyping: true}` without having to remember to pair it with
// `isOnline: true, lastSeen: now` — which is how the roster used to
// end up with "is typing…" next to a "Active 12h ago" label. One
// invariant, one place.
export const writerPresenceUpdate = (presence) => {
    const patched = {...presence};
    if (presence?.isTyping === true) {
        patched.isOnline = true;
        if (patched.lastSeen == null) {
            patched.lastSeen = Math.floor(Date.now() / 1000);
        }
    }
    return {
        type: WRITER_PRESENCE_UPDATE,
        payload: patched, // { bidId, isOnline?, lastSeen?, isTyping? }
    };
};

// Fire-and-forget trigger for the per-writer stub auto-reply. Called by
// ChatPanel ~8-30s after the customer's first message (frontend timer).
// Backend endpoint picks a random stub from `daddy_writer_stub_message`
// and persists it via the existing chat factory → Mercure broadcast →
// ChatPanel renders the bubble through its normal incoming-message path.
export const TRIGGER_BOT_REPLY_INIT = 'TRIGGER_BOT_REPLY_INIT';
export const TRIGGER_BOT_REPLY_SUCCESS = 'TRIGGER_BOT_REPLY_SUCCESS';
export const TRIGGER_BOT_REPLY_FAILURE = 'TRIGGER_BOT_REPLY_FAILURE';

// `context` lets the caller hint at which kind of reply the composer
// should produce (first_contact / reply_price / reply_deadline /
// reply_quality / reply_generic). Frontend detects simple intent
// patterns from the customer's last message (price regex / deadline
// regex / etc.) and passes the match here; unknown / omitted values
// collapse to `reply_generic` on the backend.
export const triggerBotReply = (bidId, context = null) => createAction({
    types: [
        { type: TRIGGER_BOT_REPLY_INIT, meta: { bidId } },
        { type: TRIGGER_BOT_REPLY_SUCCESS, meta: { bidId } },
        { type: TRIGGER_BOT_REPLY_FAILURE, meta: { bidId } },
    ],
    endpoint: `/api/bidding/bids/${bidId}/trigger-bot-reply`,
    method: 'POST',
    headers: getHeaders(),
    credentials: 'include',
    body: JSON.stringify(context ? { context } : {}),
});
